<script setup>
import { useI18n } from 'vue-i18n'
import { formatDate } from '@/lib/dates'
import { isManual } from '@/lib/news'
import * as Auth from '@/services/Auth'
import NewsIcon from '@/components/NewsIcon.vue'
import NewsText from '@/components/NewsText.vue'

/**
 * The news list (see doc/news.md): a box per news, the same footprint as the notice that sits beside
 * it on the play page, with the icon of its category (or of its event) and the date.
 *
 * An automatic news carries its whole text and is read right there, so it is not clickable; a manual
 * one is longer than the line it shows, so it opens in the dialog and carries "leggi tutto". While the
 * list is on its way the boxes are sketched, instead of claiming there is nothing new.
 */
defineProps({
  news: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  /** How many boxes to sketch while loading: the size of the list that is coming. */
  skeleton: { type: Number, default: 8 },
})

const emit = defineEmits(['open'])

const { t } = useI18n()

// Read and unread are a fact of the account: a visitor has none, so their list carries no emphasis.
const canTrackRead = Auth.isAuthenticated()
</script>

<template>
  <div class="news-list">
    <template v-if="loading">
      <v-skeleton-loader
        v-for="index in skeleton"
        :key="index"
        class="news-ghost"
        type="list-item-two-line"
      />
    </template>

    <template v-else>
      <article
        v-for="item in news"
        :key="item.id"
        class="news-item"
        :class="{ 'news-item--openable': isManual(item), 'news-item--unread': canTrackRead && !item.read }"
        @click="isManual(item) && emit('open', item)"
      >
        <NewsIcon :news="item" :size="18" class="news-item__icon" />

        <div class="news-item__main">
          <div class="news-item__row">
            <div class="news-item__title" :class="{ 'text-medium-emphasis': item.read }"><NewsText :news="item" /></div>
            <div class="news-item__date">{{ formatDate(item.publishedAt) }}</div>
          </div>

          <button
            v-if="isManual(item)"
            type="button"
            class="news-item__more"
            @click.stop="emit('open', item)"
          >{{ t('news.readMore') }}</button>
        </div>
      </article>
    </template>
  </div>
</template>

<style scoped>
/* Same footprint as the alert beside it (a one-line alert), and a gap so a list of them does not
   look like a single bar. */
.news-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.news-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-height: 52px;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: rgba(var(--v-border-color), .06);
  transition: background-color .15s ease, border-color .15s ease;
}

/* Only a news that opens asks to be clicked. */
.news-item--openable {
  cursor: pointer;
}

.news-item--openable:hover {
  border-color: rgba(var(--v-theme-primary), .5);
}

/* Unread is a bar on the left plus a tint, not a heavier type: the text keeps the same size and
   weight as everything else. The amber is the one of the notice up top — the site's "look here". */
.news-item--unread {
  background: rgba(var(--v-theme-info), .14);
  box-shadow: inset 3px 0 0 #ffc107;
}

.news-item__main {
  flex: 1 1 auto;
  min-width: 0;
}

.news-item__row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.news-item__title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: .925rem;
  line-height: 1.35;
}

.news-item__date {
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: .72rem;
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), .6);
}

.news-item__more {
  padding: 0;
  margin-top: 2px;
  font-size: .78rem;
  color: rgb(var(--v-theme-primary));
  background: none;
  border: 0;
  cursor: pointer;
}

.news-item__more:hover {
  text-decoration: underline;
}

.news-item__icon {
  margin-top: 1px;
}

/* The sketch of a box: it holds the shape (and the count) of the list that is arriving. */
.news-ghost {
  padding: 0;
  border-radius: 8px;
}
</style>
